import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDialog } from '../../context/DialogContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Card, CardContent } from '../../components/ui/card'
import {
  Users,
  MagnifyingGlass,
  Plus,
  UserCircle,
  Crown,
  Eye,
  Buildings,
  EnvelopeSimple,
  Clock,
  CheckCircle,
  XCircle,
  UserSwitch
} from '@phosphor-icons/react'
import { listUsers, User, listAllInvites, Invite, resendInvite, createMaster } from '../../services/users'
import { isExpired as isDateExpired, formatDateBR } from '../../lib/date'
import { useAuth } from '../../context/AuthContext'
import { Dialog } from '../../components/ui/Dialog'

type TabType = 'users' | 'invites'

export default function UsersPage() {
  const navigate = useNavigate()
  const { showSuccess, showError } = useDialog()
  const { startImpersonation, user: currentUser } = useAuth()
  const [activeTab, setActiveTab] = useState<TabType>('users')
  const [users, setUsers] = useState<User[]>([])
  const [invites, setInvites] = useState<Invite[]>([])
  const [loading, setLoading] = useState(true)
  const [resendingInvite, setResendingInvite] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [impersonating, setImpersonating] = useState<string | null>(null)

  // Create-master modal
  const [masterModalOpen, setMasterModalOpen] = useState(false)
  const [creatingMaster, setCreatingMaster] = useState(false)
  const [masterForm, setMasterForm] = useState({ name: '', email: '', password: '' })

  async function handleCreateMaster(e: React.FormEvent) {
    e.preventDefault()
    if (masterForm.name.trim().length < 3) return showError('Erro', 'Nome deve ter pelo menos 3 caracteres')
    if (masterForm.password.length < 6) return showError('Erro', 'Senha deve ter pelo menos 6 caracteres')
    setCreatingMaster(true)
    try {
      const res = await createMaster({
        name: masterForm.name.trim(),
        email: masterForm.email.trim(),
        password: masterForm.password,
      })
      showSuccess('Convite enviado', res.message || `Verificação enviada para ${masterForm.email}. O acesso master é ativado após a confirmação.`)
      setMasterModalOpen(false)
      setMasterForm({ name: '', email: '', password: '' })
      const usersData = await listUsers()
      setUsers(usersData)
    } catch (err: any) {
      showError('Erro', err.response?.data?.error || 'Erro ao criar master')
    } finally {
      setCreatingMaster(false)
    }
  }

  async function handleImpersonate(userId: string) {
    const reason = window.prompt('Motivo da impersonação (opcional, registrado no audit log):') || undefined
    setImpersonating(userId)
    try {
      await startImpersonation({ targetUserId: userId, reason })
      navigate('/dashboard', { replace: true })
    } catch (err: any) {
      showError('Erro', err.response?.data?.error || err.message || 'Falha ao impersonar')
    } finally {
      setImpersonating(null)
    }
  }

  useEffect(() => {
    async function loadData() {
      try {
        const [usersData, invitesData] = await Promise.all([
          listUsers(),
          listAllInvites()
        ])
        setUsers(usersData)
        setInvites(invitesData)
      } catch (error) {
        console.error('Error loading data:', error)
        showError('Erro', 'Erro ao carregar dados')
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  async function handleResendInvite(inviteId: string) {
    try {
      setResendingInvite(inviteId)
      await resendInvite(inviteId)
      showSuccess('Sucesso', 'Email de convite reenviado com sucesso!')
    } catch (error: any) {
      showError('Erro', error.response?.data?.error || 'Erro ao reenviar convite')
    } finally {
      setResendingInvite(null)
    }
  }

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const metrics = {
    total: users.length,
    active: users.filter(u => u.isActive).length,
    master: users.filter(u => u.isMaster).length,
    withCompanies: users.filter(u => u.companiesCount > 0).length
  }

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Carregando usuários...</div>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Gestão de Usuários</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setMasterModalOpen(true)}
                className="gap-2 text-purple-700 border-purple-200 hover:bg-purple-50"
              >
                <Crown size={18} weight="fill" />
                Criar Master
              </Button>
              <Button
                onClick={() => navigate('/master/users/invite')}
                className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              >
                <Plus size={18} weight="bold" />
                Convidar Usuário
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Metrics */}
        <div className="grid grid-cols-4 gap-6 mb-6">
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-500 mb-1">Total de Usuários</p>
              <p className="text-3xl font-bold text-gray-900">{metrics.total}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-500 mb-1">Usuários Ativos</p>
              <p className="text-3xl font-bold text-emerald-600">{metrics.active}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-500 mb-1">Master Admins</p>
              <p className="text-3xl font-bold text-purple-600">{metrics.master}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-500 mb-1">Com Empresas</p>
              <p className="text-3xl font-bold text-blue-600">{metrics.withCompanies}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <div className="flex gap-6">
            <button
              onClick={() => setActiveTab('users')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'users'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Usuários ({users.length})
            </button>
            <button
              onClick={() => setActiveTab('invites')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'invites'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Convites Pendentes ({invites.filter(i => !i.acceptedAt).length})
            </button>
          </div>
        </div>

        {/* Search */}
        {activeTab === 'users' && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <div className="relative">
              <MagnifyingGlass
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <Input
                type="text"
                placeholder="Buscar por nome ou email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        )}

        {/* Users List */}
        {activeTab === 'users' && (
          <div className="space-y-3">
            {filteredUsers.length === 0 ? (
              <Card>
                <CardContent className="pt-16 pb-16">
                  <div className="text-center">
                    <Users size={48} className="mx-auto text-gray-300 mb-3" weight="duotone" />
                    <p className="text-gray-500 text-sm">Nenhum usuário encontrado</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              filteredUsers.map((user) => (
                <Card key={user.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                          <UserCircle size={32} className="text-emerald-600" weight="duotone" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900">{user.name || 'Sem nome'}</h3>
                            {user.isMaster && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 flex items-center gap-1">
                                <Crown size={12} weight="fill" />
                                Master
                              </span>
                            )}
                            {!user.isActive && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                Inativo
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <div className="flex items-center gap-1">
                              <EnvelopeSimple size={14} />
                              <span>{user.email}</span>
                            </div>
                            {user.companiesCount > 0 && (
                              <div className="flex items-center gap-1">
                                <Buildings size={14} />
                                <span>{user.companiesCount} empresa{user.companiesCount > 1 ? 's' : ''}</span>
                              </div>
                            )}
                            <span className="text-xs">
                              Criado em {formatDateBR(user.createdAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!user.isMaster && user.isActive && user.id !== currentUser?.id && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleImpersonate(user.id)}
                            disabled={impersonating === user.id}
                            className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
                            title="Entrar como este usuário (registrado no audit log)"
                          >
                            <UserSwitch size={16} />
                            {impersonating === user.id ? 'Entrando...' : 'Entrar como'}
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/master/users/${user.id}`)}
                          className="gap-2"
                        >
                          <Eye size={16} />
                          Ver Detalhes
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Invites List */}
        {activeTab === 'invites' && (
          <div className="space-y-3">
            {invites.length === 0 ? (
              <Card>
                <CardContent className="pt-16 pb-16">
                  <div className="text-center">
                    <EnvelopeSimple size={48} className="mx-auto text-gray-300 mb-3" weight="duotone" />
                    <p className="text-gray-500 text-sm">Nenhum convite encontrado</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              invites.map((invite) => {
                const isExpired = isDateExpired(invite.expiresAt)
                const isAccepted = !!invite.acceptedAt

                return (
                  <Card key={invite.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            isAccepted ? 'bg-green-100' : isExpired ? 'bg-red-100' : 'bg-blue-100'
                          }`}>
                            {isAccepted ? (
                              <CheckCircle size={32} className="text-green-600" weight="duotone" />
                            ) : isExpired ? (
                              <XCircle size={32} className="text-red-600" weight="duotone" />
                            ) : (
                              <Clock size={32} className="text-blue-600" weight="duotone" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-gray-900">{invite.email}</h3>
                              {isAccepted && (
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                  Aceito
                                </span>
                              )}
                              {!isAccepted && isExpired && (
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                  Expirado
                                </span>
                              )}
                              {!isAccepted && !isExpired && (
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                  Pendente
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span className="font-medium">{invite.role}</span>
                              <span>•</span>
                              <span>{invite.companyType === 'shipper' ? 'Embarcador' : 'Agente de Carga'}</span>
                              {invite.tenantName && (
                                <>
                                  <span>•</span>
                                  <span>{invite.tenantName}</span>
                                </>
                              )}
                              <span>•</span>
                              <span className="text-xs">
                                Criado em {formatDateBR(invite.createdAt)}
                              </span>
                              {!isAccepted && (
                                <>
                                  <span>•</span>
                                  <span className="text-xs">
                                    Expira em {formatDateBR(invite.expiresAt)}
                                  </span>
                                </>
                              )}
                            </div>
                            {invite.invitedByName && (
                              <p className="text-xs text-gray-400 mt-1">
                                Convidado por: {invite.invitedByName}
                              </p>
                            )}
                          </div>
                        </div>
                        {!isAccepted && !isExpired && (
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleResendInvite(invite.id)}
                              disabled={resendingInvite === invite.id}
                              className="gap-2"
                            >
                              {resendingInvite === invite.id ? 'Reenviando...' : 'Reenviar Email'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const inviteUrl = `${window.location.origin}/accept-invite?token=${invite.token}`
                                navigator.clipboard.writeText(inviteUrl)
                                showSuccess('Copiado', 'Link copiado para a área de transferência!')
                              }}
                              className="gap-2"
                            >
                              Copiar Link
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )
              })
            )}
          </div>
        )}
      </main>

      {/* Create Master modal */}
      <Dialog isOpen={masterModalOpen} onClose={() => setMasterModalOpen(false)} maxWidth="md">
        <form onSubmit={handleCreateMaster} className="p-6">
          <div className="flex items-center gap-2 mb-1">
            <Crown size={22} className="text-purple-600" weight="fill" />
            <h2 className="text-lg font-semibold text-gray-900">Criar Admin Master</h2>
          </div>
          <p className="text-sm text-gray-500 mb-5">
            Enviaremos um email de verificação. O acesso master só é ativado depois que a pessoa confirmar pelo link. A senha é usada caso o email ainda não tenha conta.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
              <Input
                value={masterForm.name}
                onChange={(e) => setMasterForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Nome completo"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <Input
                type="email"
                value={masterForm.email}
                onChange={(e) => setMasterForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="email@dominio.com"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
              <Input
                type="password"
                value={masterForm.password}
                onChange={(e) => setMasterForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="Mínimo 6 caracteres"
                minLength={6}
                required
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button type="button" variant="outline" onClick={() => setMasterModalOpen(false)} disabled={creatingMaster}>
              Cancelar
            </Button>
            <Button type="submit" disabled={creatingMaster} className="gap-2 bg-purple-600 hover:bg-purple-700">
              <Crown size={16} weight="fill" />
              {creatingMaster ? 'Criando...' : 'Criar Master'}
            </Button>
          </div>
        </form>
      </Dialog>
    </AppLayout>
  )
}
